import moment from "moment";
import getEvent from "./eventModel";
import { dateFormat, dayFormat } from "../tools";
import { v4 as uuidv4 } from "uuid";

// Fill the calendar with events
const getFilledEvents = (event) => {
  // fill a day
  const timeEvents = Object.keys(event)
    .filter((key) => key.startsWith("start"))
    .map((key) => key.split("start")[1])
    .map((index) =>
      getEvent({
        type: "DUPLICATE_EVENT",
        payload: {
          start: event["start" + index],
          end: event["end" + index],
          event,
        },
      })
    );
  // fill a range
  const startDay = moment(event.start, "YYYY-MM-DD");
  const endDay = moment(event.endDay);
  const diff = endDay.diff(startDay, "days") + 1;
  let eventsToAdd = timeEvents.flatMap((timeEvent) =>
    new Array(diff).fill(0).map((_, index) => ({
      ...timeEvent,
      id: uuidv4(),
      start: moment(timeEvent.start, dateFormat)
        .add(index, "days")
        .format(dateFormat),
      end:
        timeEvent.end === null
          ? null
          : moment(timeEvent.end, dateFormat)
              .add(index, "days")
              .format(dateFormat),
    }))
  );
  eventsToAdd[0].id = event.id; // replace the first random generated eventId with the highlighted event
  return eventsToAdd;
};

// copy calendar events to another range
const getCopy = (events, action) => {
  const momentFromStart = moment(action.payload.from.start, dayFormat);
  const momentFromEnd = moment(action.payload.from.endDay, dayFormat);
  const fromDates = Array(momentFromEnd.diff(momentFromStart, "days") + 1)
    .fill()
    .map((_, i) => momentFromStart.clone().add(i, "day").format(dayFormat));
  const momentToStart = moment(action.payload.to.start, dayFormat);
  const momentToEnd = moment(action.payload.to.endDay, dayFormat);
  const toDates = Array(momentToEnd.diff(momentToStart, "days") + 1)
    .fill()
    .map((_, i) => momentToStart.clone().add(i, "day").format(dayFormat));

  const fromDatesEvents = fromDates.map((fromDate) =>
    events.filter((event) => moment(event.start).isSame(fromDate, "date"))
  );
  const eventsToAdd = toDates.map((date, index) =>
    fromDatesEvents[index % fromDatesEvents.length].map((event) =>
      getEvent({
        type: "DUPLICATE_EVENT",
        payload: {
          event,
          start: moment(event.start, dateFormat)
            .set({
              year: moment(date, dayFormat).year(),
              month: moment(date, dayFormat).month(),
              date: moment(date, dayFormat).date(),
            })
            .format(dateFormat),
          end:
            event.end === null
              ? null
              : moment(event.end, dateFormat)
                  .set({
                    year: moment(date, dayFormat).year(),
                    month: moment(date, dayFormat).month(),
                    date: moment(date, dayFormat).date(),
                  })
                  .format(dateFormat),
        },
      })
    )
  );
  return eventsToAdd.flat();
};

export default function eventsReducer(events, action) {
  switch (action.type) {
    case "SET_EVENTS":
      return action.payload;
    case "ADD_EVENT":
      return [...events, action.payload];
    case "FILL_EVENTS":
      return [
        ...action.payload.tempEvents,
        ...getFilledEvents(action.payload.event),
      ];
    case "COPY_EVENTS":
      return [...events, ...getCopy(events, action)];

    case "DELETE_RANGE":
      return events.filter(
        (item) =>
          !moment(item.start, dateFormat).isBetween(
            moment(action.payload.start, dateFormat).startOf("date"),
            moment(action.payload.endDay, dateFormat).endOf("date")
          )
      );

    case "DELETE_THIS":
      return events.filter((item) => item.id !== action.payload.id);

    case "DELETE_DAY":
      return events.filter((item) => {
        if (
          moment(item.start, dateFormat).isAfter(moment(), "date") &&
          item.itemId === action.payload.itemId &&
          moment(item.start, dateFormat).format("ddd") ===
            moment(action.payload.start, dateFormat).format("ddd")
        )
          return false;
        return true;
      });

    case "DELETE_ALL":
      return events.filter((item) => {
        if (
          moment(item.start, dateFormat).isSameOrAfter(moment(), "date") &&
          item.itemId === action.payload.itemId
        )
          return false;
        return true;
      });

    case "UPDATE_EVENT":
      const index = events.findIndex((event) => event.id === action.payload.id);
      let updatedEvents = [...events]; // important to create a copy, otherwise you'll modify state outside of setState call
      updatedEvents[index] = { ...updatedEvents[index], ...action.payload };
      return updatedEvents;

    default:
      return events;
  }
}
